import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';

interface Props {
  children: string;
  className?: string;
}

export default function MarkdownView({ children, className = '' }: Props) {
  return (
    <div className={`md-view ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeSanitize]}
        components={{
          h1: ({ children }) => <h1 className="font-display font-bold text-[20px] tracking-tight text-ink mt-4 mb-2">{children}</h1>,
          h2: ({ children }) => <h2 className="font-display font-bold text-[17px] tracking-tight text-ink mt-3 mb-1.5">{children}</h2>,
          h3: ({ children }) => <h3 className="font-display font-bold text-[15px] tracking-tight text-ink mt-2.5 mb-1">{children}</h3>,
          h4: ({ children }) => <h4 className="font-display font-bold text-[13px] tracking-tight text-ink mt-2 mb-1">{children}</h4>,
          p:  ({ children }) => <p className="font-serif text-[14px] leading-[1.7] text-ink mb-3">{children}</p>,
          ul: ({ children }) => <ul className="list-disc list-outside pl-6 mb-3 space-y-1">{children}</ul>,
          ol: ({ children }) => <ol className="list-decimal list-outside pl-6 mb-3 space-y-1">{children}</ol>,
          li: ({ children }) => <li className="font-serif text-[14px] leading-[1.6] text-ink">{children}</li>,
          strong: ({ children }) => <strong className="font-bold text-ink">{children}</strong>,
          em: ({ children }) => <em className="italic text-ink-2">{children}</em>,
          blockquote: ({ children }) => (
            <blockquote className="border-l-4 border-brass bg-brass-soft/30 pl-4 pr-3 py-2 my-3 font-serif text-[13px] text-ink-2 italic">
              {children}
            </blockquote>
          ),
          code: ({ inline, children }: any) =>
            inline ? (
              <code className="font-mono text-[12px] px-1 py-0.5 bg-brass-soft/40 text-brass border border-brass/30">
                {children}
              </code>
            ) : (
              <code className="block font-mono text-[12px] p-3 bg-paper-2/60 text-ink-2 border border-ink-line overflow-x-auto whitespace-pre">
                {children}
              </code>
            ),
          pre: ({ children }) => <pre className="my-3">{children}</pre>,
          a: ({ children, href }) => (
            <a href={href} target="_blank" rel="noopener noreferrer" className="text-brass underline decoration-dotted hover:decoration-solid">
              {children}
            </a>
          ),
          table: ({ children }) => (
            <div className="my-3 overflow-x-auto">
              <table className="w-full border border-ink-line font-mono text-[11px]">{children}</table>
            </div>
          ),
          thead: ({ children }) => <thead className="bg-paper-2/60 border-b border-ink">{children}</thead>,
          th: ({ children }) => <th className="text-left px-3 py-2 text-ink-3 uppercase tracking-wider text-[10px] border-r border-ink-line last:border-r-0">{children}</th>,
          td: ({ children }) => <td className="px-3 py-2 border-r border-ink-line/60 last:border-r-0 border-b border-dashed border-ink-line/40">{children}</td>,
          hr: () => <hr className="my-4 border-t border-dashed border-ink-line" />,
        }}
      >
        {children || ''}
      </ReactMarkdown>
    </div>
  );
}
